import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, X } from "lucide-react";

export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "danger",
  onConfirm,
  onCancel,
}) {
  if (!open) return null;
  return <OpenDialog {...{ title, message, confirmLabel, cancelLabel, tone, onConfirm, onCancel }} />;
}

function OpenDialog({ title, message, confirmLabel, cancelLabel, tone, onConfirm, onCancel }) {
  const cancelRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    cancelRef.current?.focus();
    const closeOnEscape = (event) => { if (event.key === "Escape") onCancel(); };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [onCancel]);
  const confirmOnce = async () => {
    if (submitting) return;
    setSubmitting(true);
    try { await onConfirm(); } finally { setSubmitting(false); }
  };
  const danger = tone === "danger";
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="site-confirm-dialog" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onCancel(); }}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" aria-describedby="confirm-message">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${danger ? "bg-red-50 text-[#C05746]" : "bg-amber-50 text-amber-700"}`}>
              <AlertTriangle className="h-5 w-5" />
            </div>
            <h3 id="confirm-title" className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>{title}</h3>
          </div>
          <button type="button" disabled={submitting} onClick={onCancel} className="text-[#8A938D] disabled:opacity-50" aria-label="Close confirmation">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div id="confirm-message" className="text-sm text-[#5C635E] mt-4 whitespace-pre-line">{message}</div>
        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={confirmOnce}
            disabled={submitting}
            aria-busy={submitting || undefined}
            className={`${danger ? "bg-[#C05746] hover:bg-[#A9483A]" : "bg-[#4A5D4E] hover:bg-[#3B4A3E]"} text-white rounded-xl px-5 py-2.5 text-sm disabled:opacity-50`}
          >
            {submitting ? `${confirmLabel}…` : confirmLabel}
          </button>
          <button ref={cancelRef} type="button" disabled={submitting} onClick={onCancel} className="text-[#5C635E] text-sm px-4 py-2.5 rounded-xl hover:bg-[#F4F1EA] disabled:opacity-50">{cancelLabel}</button>
        </div>
      </div>
    </div>
  );
}
